import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { LogOut, Plus, Tag } from 'lucide-react';
import { toast } from 'sonner';
import { CouponBrowser } from '../components/CouponBrowser';
import {
  MerchantStatsCards, MerchantAnalyticsPanel, MerchantCouponList,
  MerchantTestimonials, MerchantAudiobooks
} from '../components/merchant';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const STATES = ['AC','AL','AP','AM','BA','CE','DF','ES','GO','MA','MT','MS','MG','PA','PB','PR','PE','PI','RJ','RN','RS','RO','RR','SC','SP','SE','TO'];

const WhatsAppIcon = ({ className = "h-5 w-5" }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
  </svg>
);

export default function MerchantDashboard() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [user, setUser] = useState(null);
  const [coupons, setCoupons] = useState([]);
  const [stats, setStats] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [testimonials, setTestimonials] = useState([]);
  const [audiobooks, setAudiobooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showShareDialog, setShowShareDialog] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortBy, setSortBy] = useState('newest');
  const [formData, setFormData] = useState({
    title: '', description: '', discount_percentage: '', category: '',
    products: '', expires_at: '', max_uses: '', merchant_whatsapp: ''
  });
  const [selectedImages, setSelectedImages] = useState([]);
  const [imagePreview, setImagePreview] = useState([]);

  useEffect(() => { fetchData(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Poll Stripe payment status
  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    if (!sessionId) return;
    const token = localStorage.getItem('session_token');
    const hdrs = { Authorization: `Bearer ${token}` };
    let attempts = 0;
    const poll = async () => {
      if (attempts >= 8) return;
      try {
        const res = await axios.get(`${BACKEND_URL}/api/subscription/status/${sessionId}`, { headers: hdrs });
        if (res.data.payment_status === 'paid') {
          toast.success(`Plano ${res.data.plan} ativado com sucesso!`);
          fetchData();
          navigate('/merchant', { replace: true });
          return;
        }
        if (res.data.status === 'expired') { toast.error('Sessao de pagamento expirou'); return; }
      } catch (err) { console.warn('Payment poll error:', err.message); }
      attempts++;
      setTimeout(poll, 2000);
    };
    poll();
  }, [searchParams]);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('session_token');
      const headers = { Authorization: `Bearer ${token}` };
      const [userRes, couponsRes, statsRes, testimonialsRes, analyticsRes, abRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/auth/me`, { headers }),
        axios.get(`${BACKEND_URL}/api/merchant/coupons`, { headers }),
        axios.get(`${BACKEND_URL}/api/stats`, { headers }),
        axios.get(`${BACKEND_URL}/api/merchant/testimonials`, { headers }),
        axios.get(`${BACKEND_URL}/api/merchant/analytics`, { headers }).catch(() => ({ data: null })),
        axios.get(`${BACKEND_URL}/api/audiobooks/merchant`, { headers }).catch(() => ({ data: [] }))
      ]);
      setUser(userRes.data);
      setCoupons(couponsRes.data);
      setStats(statsRes.data);
      setTestimonials(testimonialsRes.data);
      setAnalytics(analyticsRes.data);
      setAudiobooks(abRes.data);
      if (userRes.data.whatsapp) setFormData(prev => ({ ...prev, merchant_whatsapp: userRes.data.whatsapp }));
      if (userRes.data.merchant_category) setFormData(prev => ({ ...prev, category: userRes.data.merchant_category }));
    } catch (error) {
      // error handled by toast
      toast.error('Erro ao carregar dados');
    } finally { setLoading(false); }
  };

  const handleLogout = async () => {
    try {
      const token = localStorage.getItem('session_token');
      await axios.post(`${BACKEND_URL}/api/auth/logout`, {}, { headers: { Authorization: `Bearer ${token}` } });
      localStorage.removeItem('session_token');
      navigate('/');
    } catch (error) { /* logout error non-blocking */ }
  };

  const handleImageSelect = (e) => {
    const files = Array.from(e.target.files);
    if (files.length + selectedImages.length > 3) { toast.error('Maximo de 3 imagens permitidas'); return; }
    const newImages = []; const newPreviews = [];
    files.forEach((file) => {
      const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
      if (file.size > MAX_FILE_SIZE) { toast.error('Imagem muito grande. Maximo 5MB.'); return; }
      const reader = new FileReader();
      reader.onloadend = () => {
        newImages.push(reader.result); newPreviews.push(reader.result);
        if (newImages.length === files.length) {
          setSelectedImages([...selectedImages, ...newImages]);
          setImagePreview([...imagePreview, ...newPreviews]);
        }
      };
      reader.readAsDataURL(file);
    });
  };

  const removeImage = (index) => {
    setSelectedImages(selectedImages.filter((_, i) => i !== index));
    setImagePreview(imagePreview.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('session_token');
      const payload = {
        ...formData, discount_percentage: parseInt(formData.discount_percentage),
        max_uses: parseInt(formData.max_uses) || 0,
        city: user?.city || '', state: user?.state || '',
        products: formData.products.split(',').map(p => p.trim()), images: selectedImages
      };
      await axios.post(`${BACKEND_URL}/api/coupons`, payload, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Cupom criado com sucesso!');
      setShowCreateDialog(false);
      fetchData();
      setFormData({ title: '', description: '', discount_percentage: '', category: user?.merchant_category || '', products: '', expires_at: '', max_uses: '', merchant_whatsapp: user?.whatsapp || '' });
      setSelectedImages([]); setImagePreview([]);
    } catch (error) {
      const msg = error.response?.data?.detail || 'Erro ao criar cupom';
      toast.error(msg);
    }
  };

  const handleDelete = async (couponId) => {
    if (!confirm('Deseja realmente excluir este cupom?')) return;
    try {
      const token = localStorage.getItem('session_token');
      await axios.delete(`${BACKEND_URL}/api/coupons/${couponId}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Cupom excluido');
      fetchData();
    } catch { toast.error('Erro ao excluir cupom'); }
  };

  const handleShareCoupon = (coupon) => {
    const msg = `Confira este cupom de desconto no ClubdoCupom!\n\n*${coupon.title}*\nDesconto: *${coupon.discount_percentage}%*\nCategoria: ${coupon.category}\n\nAcesse: www.clubdocupom.com`;
    window.open(`https://wa.me/?text=${encodeURIComponent(msg)}`, '_blank');
  };

  const handleShareApp = () => {
    const msg = `Aprenda a Ganhar descontos exclusivos que so o ClubdoCupom pode oferecer, rapido, facil e na palma da sua mao!\n\nAcesse: www.clubdocupom.com`;
    window.open(`https://wa.me/?text=${encodeURIComponent(msg)}`, '_blank');
    setShowShareDialog(false);
  };

  const generateCouponImage = (coupon) => {
    const W = 1080, H = 1920;
    const canvas = document.createElement('canvas');
    canvas.width = W; canvas.height = H;
    const ctx = canvas.getContext('2d');
    const grad = ctx.createLinearGradient(0, 0, 0, H);
    grad.addColorStop(0, '#0f172a'); grad.addColorStop(0.5, '#1e293b'); grad.addColorStop(1, '#0f172a');
    ctx.fillStyle = grad; ctx.fillRect(0, 0, W, H);
    ctx.fillStyle = '#334155'; ctx.fillRect(0, 0, W, 8);
    ctx.textAlign = 'center'; ctx.fillStyle = '#ffffff'; ctx.font = 'bold 64px sans-serif';
    ctx.fillText('ClubdoCupom', W / 2, 120);
    ctx.fillStyle = '#94a3b8'; ctx.font = '36px sans-serif';
    ctx.fillText(coupon.merchant_name || user?.company_name || user?.name || '', W / 2, 180);
    ctx.strokeStyle = '#475569'; ctx.lineWidth = 2; ctx.beginPath(); ctx.moveTo(100, 220); ctx.lineTo(W - 100, 220); ctx.stroke();

    const drawRest = (imgLoaded, img) => {
      let y = 260;
      if (imgLoaded && img) {
        const iW = W - 160, iH = iW * 0.75;
        ctx.save();
        const r = 20;
        ctx.beginPath(); ctx.moveTo(80 + r, y); ctx.lineTo(80 + iW - r, y); ctx.quadraticCurveTo(80 + iW, y, 80 + iW, y + r);
        ctx.lineTo(80 + iW, y + iH - r); ctx.quadraticCurveTo(80 + iW, y + iH, 80 + iW - r, y + iH);
        ctx.lineTo(80 + r, y + iH); ctx.quadraticCurveTo(80, y + iH, 80, y + iH - r);
        ctx.lineTo(80, y + r); ctx.quadraticCurveTo(80, y, 80 + r, y); ctx.closePath(); ctx.clip();
        ctx.drawImage(img, 80, y, iW, iH); ctx.restore(); y += iH + 50;
      }
      ctx.textAlign = 'center'; ctx.fillStyle = '#94a3b8'; ctx.font = 'italic 32px sans-serif';
      ctx.fillText('Acesse e economize:', W / 2, y + 10);
      ctx.fillStyle = '#ffffff'; ctx.font = 'bold 44px sans-serif';
      ctx.fillText('clubdocupom.com', W / 2, y + 65); y += 120;
      ctx.fillStyle = '#ef4444'; ctx.font = 'bold 160px sans-serif';
      ctx.fillText(`${coupon.discount_percentage}%`, W / 2, y + 130);
      ctx.font = 'bold 56px sans-serif'; ctx.fillText('DE DESCONTO', W / 2, y + 200); y += 260;
      ctx.fillStyle = '#ffffff'; ctx.font = 'bold 52px sans-serif';
      const wrapText = (text, maxW) => { const words = text.split(' '); const lines = []; let line = '';
        for (const w of words) { const t = line + (line ? ' ' : '') + w; if (ctx.measureText(t).width > maxW && line) { lines.push(line); line = w; } else line = t; }
        if (line) lines.push(line); return lines; };
      const tLines = wrapText(coupon.title, W - 160);
      tLines.forEach((l, i) => ctx.fillText(l, W / 2, y + i * 64)); y += tLines.length * 64 + 30;
      ctx.fillStyle = '#cbd5e1'; ctx.font = '34px sans-serif';
      const dLines = wrapText(coupon.description || '', W - 160);
      dLines.slice(0, 3).forEach((l, i) => ctx.fillText(l, W / 2, y + i * 46)); y += Math.min(dLines.length, 3) * 46 + 40;
      ctx.fillStyle = 'rgba(51,65,85,0.5)';
      ctx.beginPath(); const rr = 16; ctx.moveTo(80+rr,y); ctx.lineTo(W-80-rr,y); ctx.quadraticCurveTo(W-80,y,W-80,y+rr);
      ctx.lineTo(W-80,y+200-rr); ctx.quadraticCurveTo(W-80,y+200,W-80-rr,y+200); ctx.lineTo(80+rr,y+200);
      ctx.quadraticCurveTo(80,y+200,80,y+200-rr); ctx.lineTo(80,y+rr); ctx.quadraticCurveTo(80,y,80+rr,y); ctx.closePath(); ctx.fill();
      ctx.fillStyle = '#e2e8f0'; ctx.font = '30px sans-serif'; ctx.textAlign = 'left';
      ctx.fillText(`Categoria: ${coupon.category || ''}`, 120, y + 50);
      ctx.fillText(`Local: ${coupon.city || ''}, ${coupon.state || ''}`, 120, y + 95);
      ctx.fillText(`Valido ate: ${new Date(coupon.expires_at).toLocaleDateString('pt-BR')}`, 120, y + 140);
      if (coupon.products?.length > 0) ctx.fillText(`Produtos: ${coupon.products.slice(0, 3).join(', ')}`, 120, y + 185);
      ctx.fillStyle = '#334155'; ctx.fillRect(0, H - 8, W, 8);
      canvas.toBlob((blob) => {
        const url = URL.createObjectURL(blob); const a = document.createElement('a');
        a.href = url; a.download = `cupom_${coupon.title.replace(/\s+/g, '_').toLowerCase()}.png`;
        a.click(); URL.revokeObjectURL(url); toast.success('Imagem do cupom baixada!');
      }, 'image/png');
    };
    if (coupon.images?.length > 0) {
      const img = new Image(); img.crossOrigin = 'anonymous';
      img.onload = () => drawRest(true, img); img.onerror = () => drawRest(false, null);
      img.src = coupon.images[0];
    } else drawRest(false, null);
  };

  const filteredCoupons = coupons.filter(c => {
    if (statusFilter === 'active') return c.is_active && !c.is_deleted;
    if (statusFilter === 'inactive') return !c.is_active && !c.is_deleted;
    if (statusFilter === 'deleted') return c.is_deleted;
    return true;
  }).sort((a, b) => {
    if (sortBy === 'most_used') return (b.current_uses || 0) - (a.current_uses || 0);
    if (sortBy === 'least_used') return (a.current_uses || 0) - (b.current_uses || 0);
    return 0;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-slate-50">
        <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-slate-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 overflow-x-hidden">
      <header className="bg-white/80 backdrop-blur-md border-b border-slate-200/50 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 sm:py-4">
          <div className="flex justify-between items-center">
            <div className="min-w-0">
              <h1 className="text-lg sm:text-2xl font-black text-slate-900 truncate" data-testid="merchant-dashboard-title">Dashboard do Lojista</h1>
              <p className="text-xs sm:text-sm text-slate-600 truncate">{user?.name}</p>
            </div>
            <div className="flex items-center gap-1 sm:gap-2 shrink-0">
              <Button variant="ghost" size="sm" onClick={() => navigate('/edit-profile')} className="text-slate-600 hover:text-slate-600 px-2 sm:px-3" data-testid="edit-profile-btn">
                <span className="hidden sm:inline">Editar Perfil</span><span className="sm:hidden text-xs">Perfil</span>
              </Button>
              {user?.role === 'admin' && (
                <Button variant="ghost" size="sm" onClick={() => navigate('/admin')} className="text-red-500 hover:text-red-600 px-2 sm:px-3" data-testid="admin-panel-btn">Admin</Button>
              )}
              <Button variant="ghost" size="sm" onClick={handleLogout} className="text-slate-600 hover:text-slate-600 px-2 sm:px-3" data-testid="logout-btn">
                <LogOut className="h-5 w-5" /><span className="hidden sm:inline ml-2">Sair</span>
              </Button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 overflow-x-hidden">
        <MerchantStatsCards stats={stats} user={user} navigate={navigate} />
        <MerchantAnalyticsPanel analytics={analytics} />

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row flex-wrap gap-3 mb-6">
          <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
            <DialogTrigger asChild>
              <Button className="bg-slate-500 hover:bg-slate-600 text-white w-full sm:w-auto" data-testid="create-coupon-btn">
                <Plus className="h-5 w-5 mr-2" /> Criar Novo Cupom
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto bg-slate-50 dialog-content-override mx-2 sm:mx-auto">
              <DialogHeader><DialogTitle>Criar Novo Cupom</DialogTitle></DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div><Label htmlFor="title">Titulo</Label><Input id="title" value={formData.title} onChange={(e) => setFormData({...formData, title: e.target.value})} required data-testid="coupon-title-input" /></div>
                <div><Label htmlFor="description">Descricao</Label><Textarea id="description" value={formData.description} onChange={(e) => setFormData({...formData, description: e.target.value})} required data-testid="coupon-description-input" /></div>
                <div><Label htmlFor="discount">Desconto (%)</Label><Input id="discount" type="number" min="1" max="100" value={formData.discount_percentage} onChange={(e) => setFormData({...formData, discount_percentage: e.target.value})} required data-testid="coupon-discount-input" /></div>
                <div><Label htmlFor="products">Produtos (separados por virgula)</Label><Input id="products" value={formData.products} onChange={(e) => setFormData({...formData, products: e.target.value})} placeholder="Ex: Furadeira, Parafusadeira" required data-testid="coupon-products-input" /></div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div><Label>Estado</Label>
                    <Input value={user?.state || ''} disabled className="mt-1 bg-slate-100" data-testid="coupon-state-display" />
                  </div>
                  <div>
                    <Label>Cidade</Label>
                    <Input value={user?.city || ''} disabled className="mt-1 bg-slate-100" data-testid="coupon-city-display" />
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div><Label>Data de Expiracao</Label><Input type="date" value={formData.expires_at} onChange={(e) => setFormData({...formData, expires_at: e.target.value})} required data-testid="coupon-expires-input" /></div>
                  <div><Label>Limite de Usos (0 = ilimitado)</Label><Input type="number" min="0" value={formData.max_uses} onChange={(e) => setFormData({...formData, max_uses: e.target.value})} data-testid="coupon-max-uses-input" /></div>
                </div>
                <div><Label>WhatsApp para Contato</Label><Input type="tel" value={formData.merchant_whatsapp} onChange={(e) => setFormData({...formData, merchant_whatsapp: e.target.value})} placeholder="+5511999999999" required data-testid="coupon-whatsapp-input" /></div>
                <div>
                  <Label>Imagens do Cupom (maximo 3)</Label>
                  <Input type="file" accept="image/*" multiple onChange={handleImageSelect} disabled={selectedImages.length >= 3} data-testid="coupon-images-input" className="mt-2" />
                  <p className="text-xs text-slate-500 mt-1">{selectedImages.length}/3 imagens. Recomendado: 800x600px</p>
                  {imagePreview.length > 0 && (
                    <div className="grid grid-cols-3 gap-2 mt-4">
                      {imagePreview.map((preview, index) => (
                        <div key={index} className="relative group aspect-[4/3] overflow-hidden rounded-md border border-slate-200">
                          <img src={preview} alt={`Preview ${index + 1}`} className="w-full h-full object-cover" />
                          <button type="button" onClick={() => removeImage(index)} className="absolute top-1 right-1 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity" data-testid={`remove-image-${index}`}>x</button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <Button type="submit" className="w-full bg-slate-500 hover:bg-slate-600" data-testid="submit-coupon-btn">Criar Cupom</Button>
              </form>
            </DialogContent>
          </Dialog>

          <Dialog open={showShareDialog} onOpenChange={setShowShareDialog}>
            <DialogTrigger asChild>
              <Button className="bg-green-600 hover:bg-green-700 text-white w-full sm:w-auto" data-testid="share-app-btn">
                <WhatsAppIcon className="h-5 w-5 mr-2" /> Compartilhar ClubdoCupom
              </Button>
            </DialogTrigger>
            <DialogContent className="!bg-white max-w-md">
              <DialogHeader><DialogTitle className="text-xl font-bold text-slate-900">Compartilhar ClubdoCupom</DialogTitle></DialogHeader>
              <div className="space-y-4 mt-4">
                <div className="bg-green-50 border border-green-200 rounded-md p-4">
                  <p className="text-slate-700 leading-relaxed">Aprenda a Ganhar descontos exclusivos que so o ClubdoCupom pode oferecer, rapido, facil e na palma da sua mao!</p>
                  <p className="text-sm text-green-700 font-semibold mt-2">www.clubdocupom.com</p>
                </div>
                <Button onClick={handleShareApp} className="w-full bg-green-600 hover:bg-green-700 text-white font-bold py-3" data-testid="confirm-share-app-btn">
                  <WhatsAppIcon className="h-5 w-5 mr-2" /> Enviar pelo WhatsApp
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>

        <MerchantCouponList filteredCoupons={filteredCoupons} statusFilter={statusFilter} setStatusFilter={setStatusFilter}
          sortBy={sortBy} setSortBy={setSortBy} handleDelete={handleDelete} handleShareCoupon={handleShareCoupon}
          generateCouponImage={generateCouponImage} />

        <div className="mt-12" data-testid="merchant-coupon-browser-section">
          <div className="flex items-center gap-3 mb-6">
            <Tag className="h-6 w-6 text-blue-600" />
            <h2 className="text-2xl font-bold text-slate-900">Buscar Cupons</h2>
          </div>
          <Card className="bg-white border border-slate-200"><CardContent className="pt-6"><CouponBrowser defaultCity={user?.city} defaultState={user?.state} /></CardContent></Card>
        </div>

        <div className="mt-12"><MerchantAudiobooks audiobooks={audiobooks} /></div>
        <div className="mt-12"><MerchantTestimonials testimonials={testimonials} /></div>
      </div>
    </div>
  );
}
